import { createContext, useContext, useId } from 'react';
import type { PropsWithChildren } from 'react';

import { isWeb, withStaticProperties } from '@tamagui/core';

import { Box } from '../Box';
import type { BoxProps } from '../Box';
import { Button } from '../Button';
import type { ButtonProps } from '../Button';
import type { SubmitHandlerProp } from './types';

type FormContextProps = SubmitHandlerProp & {
  /**
   * The id of the form
   */
  formId?: string;
};

// Using context so we can pass the onSubmit function to the form or button,
// depending on the platform
const FormContext = createContext<FormContextProps>({ onSubmit: () => {}, formId: '' });

type FormProps = PropsWithChildren<FormContextProps>;

const FormProvider = ({ onSubmit, formId, children }: FormProps) => {
  const backupId = useId();
  const id = formId || backupId;

  return <FormContext.Provider value={{ onSubmit, formId: id }}>{children}</FormContext.Provider>;
};

type FormComponentProps = Omit<BoxProps, 'tag'>;

const FormComponent = ({ children, ...props }: FormComponentProps) => {
  const { onSubmit, formId } = useContext(FormContext);
  return (
    <Box
      tag="form"
      id={formId}
      // @ts-expect-error onSubmit doesn't exist on BoxProps, but passing
      // the prop still works on web
      onSubmit={isWeb ? onSubmit : undefined}
      {...props}
    >
      {children}
    </Box>
  );
};

type FormButtonProps = Omit<ButtonProps, 'type' | 'onPress'>;

const FormButton = (props: FormButtonProps) => {
  const { onSubmit, formId } = useContext(FormContext);
  const handlePress: ButtonProps['onPress'] = (event) => {
    onSubmit(event as unknown as Parameters<typeof onSubmit>[0]);
  };

  return (
    <Button
      type="submit"
      form={isWeb ? formId : undefined}
      onPress={isWeb ? undefined : handlePress}
      {...props}
    />
  );
};

// Basic cross-platform Form component. Lets us place the `<button>` outside the
// `<form>` element if required. Currently only used internally.
const Form = withStaticProperties(FormProvider, {
  Form: FormComponent,
  Button: FormButton,
});

export { Form };
export type { FormProps, FormComponentProps, FormButtonProps };
